import React, { useEffect, useState, useMemo } from 'react';
import '../css/stats.css';


import LRUCache from '../components/cache.js'
import StatDisplay from '../components/statDisplay.js'
const cacheRef = new LRUCache(50);

const StatWrapper = ({ base, modifiers, buffs, context, setTrueStats: passTrueStats }) => {
  const [calculatedStats, setCalculatedStats] = useState(null);
  const [showExportModal, setShowExportModal] = useState(false);

  const payload = useMemo(
  () => JSON.stringify({ base, modifiers, buffs, context }),
  [base, modifiers, buffs, context]
  );

  useEffect(() => {
    const cached = cacheRef.get(payload);
    if (cached) {
      setCalculatedStats(cached);
      return;
    }

    const fetchStats = async () => {
      try {
        const res = await fetch('/api/stats/getTrueStats', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: payload,
        });

        if (!res.ok) throw new Error('Failed to calculate stats');
        const data = await res.json();

        cacheRef.set(payload, data);
        setCalculatedStats(data);
      } catch (err) {
        console.error('Error fetching stats:', err);
      }
    };

    fetchStats();
  }, [payload]);

  const statGroups = {
    ATK: {
      Flat: ['ATK', 'ElementalATK'],
      "Additive (Group 1)": ['ATKPercent'],
      "Flat (Group 2)": ['FlatATK'],
      "Additive (Group 2)": ['ATKIncrease'],
    },
    DEF: {
      Flat: ['DEF'],
      "Additive (Group 1)": ['DEFPercent'],
      "Flat (Group 2)": ['FlatDEF'],
      "Additive (Group 2)": ['DEFIncrease'],
    },
    HP: {
      Flat: ['HP'],
      "Additive (Group 1)": ['HPPercent'],
      "Flat (Group 2)": ['FlatHP'],
      "Additive (Group 2)": ['HPIncrease'],
    },
    DebuffAmp: {
      Additive: ['DebuffAmp'],
      Multiplicative: ['MultDebuffAmp'],
    },
    BuffAmp: {
      Additive: ['buffAmp'],
      Multiplicative: ['MultBuffAmp'],
    },
    MoveSpd: {
      Flat: ['MoveSpd'],
      Additive: ['MoveSpdPercent'],
    },
  };

  //Add stats that are part of categories into here so they don't render separately
  const excluded = ["none", "None", "ElementalATK", 'ATKPercent', "ATKIncrease", "FlatATK", "FlatDEF", "FlatHP", "DEFPercent", "HPPercent", "targeted", "displayATK", "finalATK", "finalHP", "finalDEF", "DEFIncrease", "HPIncrease", 'MultBuffAmp', 'MultDebuffAmp', 'MoveSpdPercent']
  const statReplace = {
    ATK: "finalATK",
    DEF: "finalDEF",
    HP: "finalHP",
  }
  const generateStatDisplays = (calculatedStats) => {
    if (!calculatedStats?.stats) return null;
    

    return Object.keys(calculatedStats.stats)
      .filter(stat => !excluded.includes(stat))
      .map(stat => (
        <StatDisplay
          key={stat}
          statKey={statReplace[stat] || stat}
          label={stat}
          calculatedStats={calculatedStats}
          categories={statGroups[stat] || { Additive: [stat] }}
        />
      ));
  };

  useEffect(() => {
    passTrueStats(calculatedStats?.stats || {});
  }, [calculatedStats, passTrueStats]);

  // Export modal component
  const ExportModal = () => {
    const [copied, setCopied] = useState(false);

    const exportRenameMap = {
      ATK: 'BaseATK',
      DEF: 'BaseDEF',
      HP: 'BaseHP',
      finalATK: 'FinalATK',
      finalDEF: 'FinalDEF',
      finalHP: 'FinalHP',
      burst: 'ElementalBurstDMG',
      BaseModifier: 'BaseModifierIncrease',
    };
    const renameBaseStats = (stats) => {
      const result = {};
      for (const [key, value] of Object.entries(stats)) {
        if (value === 0 || value === null || value === undefined) continue;
        const renamedKey = exportRenameMap[key] ?? key;
        result[renamedKey] = value;
      }

      const order = [
        // ATK
        'BaseATK', 'displayATK', 'trueATK', 'ElementalATK', 'ATKPercent', 'FlatATK', 'ATKIncrease','FinalATK','TargetedATKIncrease',
        // Additives
        'BaseModifier', 'FlatBoost', 
        // Multipliers
        'ElementalDMG', 'TotalDMG', 'SpecificATKPercent', 'MultSpecificATKPercent',
        //debuffs
        'DEFPenetration', 'DEFReduce', 'ElementalRES', 'DMGTaken',
        // Crit
        'CritRate', 'CritDMG',
        // DEF
        'BaseDEF','DEFPercent', 'FlatDEF', 'DEFIncrease', 'FinalDEF',
        // HP
        'BaseHP', 'HPPercent', 'FlatHP', 'HPIncrease', 'FinalHP',
        // Amps
        'BuffAmp', 'MultBuffAmp', 'DebuffAmp', 'MultDebuffAmp',
        // Sustain
        'Healing', 'DamageReduction', 'Shield',
        // Movement
        'MoveSpd', 'MoveSpdPercent',
        // Other
        'burst', 'ATKSpd',
        // Targeted
        'targeted'
      ];

      const sorted = {};
      for (const key of order) {
        if (key in result) sorted[key] = result[key];
      }
      for (const key of Object.keys(result)) {
        if (!(key in sorted)) sorted[key] = result[key];
      }

      return sorted;
    };

    const statsData = JSON.stringify(renameBaseStats(calculatedStats.stats), null, 2);
    const sourcesData = JSON.stringify(calculatedStats.sources, null, 2);

    const handleCopy = async (text) => {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };

    return (
      <div className="cookieModalOverlay" onClick={() => setShowExportModal(false)}>
        <div className="cookieModalContent" onClick={e => e.stopPropagation()}>
          <div className="cookieDropdown" style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div className="groupLabel">Stats</div>
              <pre style={{ margin: 0, fontSize: '12px', lineHeight: 1.6, overflowY: 'auto', maxHeight: '30vh', whiteSpace: 'pre' }}>
                {statsData}
              </pre>
              <button className="copyButton" onClick={() => handleCopy(statsData)}>
                {copied ? '✓ Copied!' : 'Copy Stats'}
              </button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <div className="groupLabel">Sources</div>
              <pre style={{ margin: 0, fontSize: '12px', lineHeight: 1.6, overflowY: 'auto', maxHeight: '30vh', whiteSpace: 'pre' }}>
                {sourcesData}
              </pre>
              <button className="copyButton" onClick={() => handleCopy(sourcesData)}>
                {copied ? '✓ Copied!' : 'Copy Sources'}
              </button>
            </div>

          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {calculatedStats ? (
        <div className="statWrapper">
          <div className="statTitle">
            <span>Stats</span>
            <button className="exportButton" onClick={() => setShowExportModal(true)} disabled={!calculatedStats}>
              Export JSON
            </button>
          </div>
          {/* Stats rendered here */}
          <div className="allStats">
            {generateStatDisplays(calculatedStats)}
          </div>
          {/* Targeted stats rendered separately */}
          {calculatedStats.stats.targeted &&
          Object.entries(calculatedStats.stats.targeted).map(([targetName, statObject]) => (
            <div className="targetedSection" key={targetName}>
              <div className='targetLabel'>
                {targetName} Attacks
              </div>
              <div className="conditionalStats">
                {generateStatDisplays({
                  stats: statObject,
                  sources: calculatedStats.sources.targeted[targetName]
                })}
              </div>
            </div>
          ))}

          {/* Export modal */}
          {showExportModal && <ExportModal />}
        </div>
      ) : (
        <p>Calculating stats...</p>
      )}
    </div>
  );

};

export default StatWrapper;